import { codeInputPlaceholder } from 'constants/Functions.constants';

import type { FlowFunction } from 'api/resources/flows/types';
import type { SmsTemplate } from 'api/resources/smsTemplates/types';
import type { AttributeResponse } from 'api/resources/variantManagement/types';
import type { FunctionCall, FunctionType, HandoffResponse } from 'api/types';
import {
  FLOW_TRANSITION_PREFIX,
  FUNCTION_PREFIX,
  HANDOFF_PREFIX,
  prefixMap,
  TWILIO_SMS_PREFIX,
  VARIANT_ATTRIBUTE_PREFIX,
} from 'components/molecules/RichTextInput/constants';
import type { ActionType } from 'components/molecules/RichTextInput/types';
import { getFuncArgs } from 'utils/functions';

export type TextPart = {
  type: ActionType;
  start: number;
  end: number;
  content: string;
};

type Pairs = { search: string; replace: string }[];

export type Options = {
  id: string;
  name: string;
  prefix: string;
  [key: string]: any;
}[];

export const getOptions = ({
  functions = [],
  flowFunctions = [],
  handoffs = [],
  smsTemplates = [],
  functionsFromFlow = [],
  attributes = [],
}: {
  functions?: FunctionCall[];
  flowFunctions?: FunctionCall[];
  handoffs?: HandoffResponse[];
  smsTemplates?: SmsTemplate[];
  functionsFromFlow?: FlowFunction[];
  attributes?: AttributeResponse[];
}) => {
  return [
    // Functions
    ...getOptionsFromResource<FunctionCall>(
      functions,
      { id: 'function_id', name: 'name', active: true },
      FUNCTION_PREFIX,
    ),
    // Handoffs
    ...getOptionsFromResource<HandoffResponse>(
      handoffs,
      { id: 'id', name: 'name' },
      HANDOFF_PREFIX,
    ),
    // SMS
    ...getOptionsFromResource<SmsTemplate>(
      smsTemplates,
      { id: 'id', name: 'template_name' },
      TWILIO_SMS_PREFIX,
    ),
    // Flows
    ...getOptionsFromResource<FunctionCall>(
      flowFunctions,
      { id: 'function_id', name: 'name' },
      FLOW_TRANSITION_PREFIX,
    ),
    ...getOptionsFromResource<FlowFunction>(
      functionsFromFlow,
      { id: 'function_id', name: 'name' },
      FLOW_TRANSITION_PREFIX,
    ),
    ...getOptionsFromResource<AttributeResponse>(
      attributes,
      { id: 'id', name: 'name' },
      VARIANT_ATTRIBUTE_PREFIX,
    ),
  ];
};

export const getOptionsFromResource = <T extends Record<string, any>>(
  data: T[],
  requiredKeyNames: { id: string; name: string; active?: boolean },
  prefix: string,
): Options => {
  const result =
    data?.map(d => ({
      id: d[requiredKeyNames.id] as string,
      name: d[requiredKeyNames.name] as string,
      ...(requiredKeyNames.active ? { active: d.active } : {}),
      prefix,
    })) ?? [];

  return result;
};

export const addTemplate = (text: string) => `{{${text}}}`;

export const addPrefixTemplate = (text: string, prefix: string) => {
  return addTemplate(`${prefix}${text}`);
};

export const extractFunctions = (value: string) => {
  const regex = /\{\{(.+?)\}\}/g;
  const matches = [...value.matchAll(regex)];

  return matches;
};

const replacePairs = (input: string, pairs: Pairs) => {
  return pairs.reduce(
    (acc, { search, replace }) => acc.replaceAll(search, replace),
    input ?? '',
  );
};

export const replaceNames = (input: string, options: Options) => {
  const pairs = options
    .filter(option => [undefined, true].includes(option.active))
    .reduce(
      (pairs, { name, id, prefix }) => [
        ...pairs,
        {
          search: addTemplate(name),
          replace: addPrefixTemplate(id, prefix),
        },
        {
          search: addPrefixTemplate(name, prefix),
          replace: addPrefixTemplate(id, prefix),
        },
      ],
      [] as Pairs,
    );

  return replacePairs(input, pairs);
};

export const replaceIds = (input: string, options: Options) => {
  const pairs = options.reduce(
    (pairs, { name, id, prefix }) => [
      ...pairs,
      {
        search: addTemplate(name),
        replace: addPrefixTemplate(name, prefix),
      },
      {
        search: addPrefixTemplate(id, prefix),
        replace: addPrefixTemplate(name, prefix),
      },
    ],
    [] as Pairs,
  );

  return replacePairs(input, pairs);
};

export const createRichText = (value: string) => {
  const matches = extractFunctions(value);
  let currentIndex = 0;
  const result: TextPart[] = [];

  matches.forEach(match => {
    if (match.index === undefined) {
      return;
    }

    // Text before the matched function
    if (match.index > currentIndex) {
      result.push({
        type: 'text',
        start: currentIndex,
        end: match.index - 1,
        content: value.substring(currentIndex, match.index),
      });
    }

    const content = match[1];

    const prefix = `${content.split(':')[0]}:`;

    const type = prefixMap[prefix];

    if (type) {
      result.push({
        type,
        start: match.index,
        end: match.index + match[0].length - 1,
        content: content.replace(prefix, ''),
      });
    } else {
      result.push({
        type: 'unknown',
        start: match.index,
        end: match.index + match[0].length - 1,
        content,
      });
    }

    currentIndex = match.index + match[0].length;
  });

  // Text after the last matched function
  if (currentIndex < value.length) {
    result.push({
      type: 'text',
      start: currentIndex,
      end: value.length - 1,
      content: value.substring(currentIndex, value.length),
    });
  }

  if (!value.match(/\s$/)) {
    result.push({
      type: 'text',
      content: ' ',
      start: currentIndex,
      end: currentIndex + 1,
    });
  }

  return result;
};

export const generateDefaultFunctionCode = ({
  functionName,
  functionType,
  code = undefined,
}: {
  functionName: string;
  functionType?: FunctionType;
  code?: string;
}) => {
  if (code) {
    return `def ${functionName}(${getFuncArgs(functionType)}):\n${code}`;
  }
  return codeInputPlaceholder(functionName, getFuncArgs(functionType));
};
